import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function DefaultUserRoleModel({ open, onClose, roleId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const modalRef = useRef(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);

    const handlePermissionChange = (permissionId) => {
        setSelectedPermissions((prevSelected) => {
            const updatedPermissions = prevSelected.includes(permissionId)
                ? prevSelected.filter((id) => id !== permissionId)
                : [...prevSelected, permissionId];
            newRole.setFieldValue("permissions", updatedPermissions);
            return updatedPermissions;
        });
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const handleClose = () => {
        onClose();
        newRole.resetForm({ values: { name: '', permissions: '' } });
        setSelectedPermissions([]);
    };

    useEffect(() => {
        const fetchDefaultUserRoles = async () => {
            if (!roleId) return; // Prevent running if no ID
            try {
                const response = await axios.get(`${apiUrl}auth-service/default-role/${roleId}`, {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });

                if (response.data.success && response.data.data) {
                    const defaultRoleData = response.data.data;
                    newRole.setValues({
                        name: defaultRoleData.name || '',
                        permissions: defaultRoleData.defaultPermissions.map((role) => role.id) || [],
                    });
                    setSelectedPermissions(defaultRoleData.defaultPermissions.map((role) => role.id));
                }
            } catch (error) {
                console.error('Error fetching permission type:', error.message);
            }
        };

        const fetchPermissions = async () => {
            try {
                const [permissionsResponse, permissionTypesResponse] = await Promise.all([
                    axios.get(`${apiUrl}auth-service/permission`, { headers: { 'Authorization': `Bearer ${bearerToken}` } }),
                    axios.get(`${apiUrl}auth-service/permission-type`, { headers: { 'Authorization': `Bearer ${bearerToken}` } }),
                ]);

                if (!permissionsResponse.data?.success || !permissionsResponse.data?.data) {
                    throw new Error('Failed to fetch permissions');
                }
                if (!permissionTypesResponse.data?.success || !permissionTypesResponse.data?.data) {
                    throw new Error('Failed to fetch permission types');
                }

                const permissions = permissionsResponse.data.data;
                const permissionTypes = permissionTypesResponse.data.data;

                // Initialize permission types with empty permission arrays
                const permissionTypeMap = permissionTypes.reduce((acc, type) => {
                    acc[type.id] = { ...type, permissions: [] };
                    return acc;
                }, {});

                // Group permissions under their respective permission types
                permissions.forEach(permission => {
                    if (permission.permissionType && permissionTypeMap[permission.permissionType]) {
                        permissionTypeMap[permission.permissionType].permissions.push(permission);
                    }
                });

                // Convert object back to array
                const mergedData = Object.values(permissionTypeMap);
                setPermissions(mergedData);
            } catch (error) {
                console.error(error.response?.data?.message || error.message);
            }
        };

        if (open) {
            fetchDefaultUserRoles();
            fetchPermissions();
        }
    }, [roleId, open]);

    const onSubmit = async (values) => {
        setIsLoading(true);
        try {
            if (roleId) {
                await axios.put(`${apiUrl}auth-service/default-role/${roleId}`, values, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                    },
                });
                toast.success('Default role updated successfully!');
            } else {
                const payload = {
                    ...values,
                    permissions: selectedPermissions,
                }
                await axios.post(`${apiUrl}auth-service/default-role`, payload, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                    },
                });
                toast.success('Default role created successfully!');
            }
        } catch (error) {
            console.error('Error inserting data:', error.message);
        } finally {
            setIsLoading(false);
            setTimeout(() => {
                handleClose();
            }, 1000);
        }
    };

    const newRole = useFormik({
        initialValues: {
            name: '',
            permissions: [],
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
            permissions: Yup.array()
                .min(1, 'At least one permission is required')
                .required('Required'),
        }),
        onSubmit,
    });

    const handleSelectAll = (typeId) => {
        const typePermissions = permissions.find(type => type.id === typeId)?.permissions || [];

        const typePermissionIds = typePermissions.map(p => p.id); // -> []

        const allSelected = typePermissionIds.length > 0 && typePermissionIds.every(id => selectedPermissions.includes(id));

        if (allSelected) {
            // Deselect all permissions of this type
            const updated = selectedPermissions.filter(id => !typePermissionIds.includes(id));
            setSelectedPermissions(updated);
            newRole.setFieldValue("permissions", updated);
        } else {
            // Select all permissions of this type
            const updated = Array.from(new Set([...selectedPermissions, ...typePermissionIds]));
            setSelectedPermissions(updated);
            newRole.setFieldValue("permissions", updated);
        }
    };

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
                {/* Background overlay */}
                <div className="absolute inset-0 bg-black opacity-30" onClick={handleClose}></div>

                {/* Modal */}
                <div className="bg-white rounded-lg w-full max-w-xl z-50 relative">
                    {/* Header */}
                    <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                        <h2 className="text-xl font-semibold text-center">Default Role</h2>
                    </div>

                    <div className="relative bg-white p-6 space-y-4 shadow-md rounded-b-lg">
                        <form onSubmit={newRole.handleSubmit} className="space-y-4">
                            {/* Name and Status Row */}
                            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Role Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Name"
                                        value={newRole.values.name}
                                        onChange={newRole.handleChange}
                                        onBlur={newRole.handleBlur}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {newRole.touched.name && newRole.errors.name && (
                                        <div className="text-red-500 text-xs mt-1">{newRole.errors.name}</div>
                                    )}
                                </div>
                            </div>

                            {/* Permissions Section */}
                            <div className="border rounded-lg shadow">
                                <div className="bg-gray-50 px-4 py-2 border-b">
                                    <h2 className="text-base font-bold">Select Permissions</h2>
                                </div>

                                {/* Scrollable permissions container */}
                                <div className="h-40 overflow-y-auto">
                                    {permissions.map((type) => (
                                        <div key={type.id} className="border-b last:border-b-0">
                                            <div className="bg-gray-100 px-4 py-1 sticky top-0 z-10">
                                                <h3 className="font-semibold text-sm">{type.name}</h3>
                                                <label className="flex items-center space-x-1 text-xs">
                                                    <input
                                                        type="checkbox"
                                                        checked={type.permissions.every(p => selectedPermissions.includes(p.id))}
                                                        onChange={() => handleSelectAll(type.id)}
                                                        className="h-3 w-3"
                                                    />
                                                    <span>Select All</span>
                                                </label>
                                            </div>
                                            {type.permissions.length > 0 ? (
                                                <div className="p-2">
                                                    <div className="grid grid-cols-2 gap-1">
                                                        {type.permissions.map((permission) => (
                                                            <label key={permission.id} className="flex items-center space-x-1 p-0.5 hover:bg-gray-50 rounded">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedPermissions && selectedPermissions.includes(permission.id)}
                                                                    onChange={() => handlePermissionChange(permission.id)}
                                                                    className="h-3 w-3"
                                                                />
                                                                <span className="text-xs">{permission.name}</span>
                                                            </label>
                                                        ))}

                                                    </div>

                                                </div>
                                            ) : (
                                                <p className="text-gray-500 p-2 text-xs">No permissions available</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {newRole.touched.permissions && newRole.errors.permissions && (
                                    <p className="text-red-500 text-xs mt-1 ml-2">{newRole.errors.permissions}</p>
                                )}
                            </div>

                            {/* Selected Permissions Summary */}
                            <div className="border rounded-lg p-3 bg-gray-50">
                                <h3 className="font-semibold mb-1 text-sm">Selected Permissions</h3>
                                {selectedPermissions && selectedPermissions.length > 0 ? (
                                    <div className="max-h-20 overflow-y-auto">
                                        <div className="flex flex-wrap gap-1">
                                            {selectedPermissions.map((id) => {
                                                const permission = permissions
                                                    .flatMap((type) => type.permissions)
                                                    .find((p) => p.id === id);

                                                return permission ? (
                                                    <div key={id} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                        {permission.name}
                                                    </div>
                                                ) : null;
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-xs">No permissions selected</p>
                                )}
                            </div>

                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="px-5 h-8 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </>
    );
}

DefaultUserRoleModel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    roleId: PropTypes.string,
};